import { Booking, Order } from '@/types/booking'

export type SplitOrders = {
  allOrders: Order[]
  upcomingOrders: Order[]
}

/**
 * Identity for an order within the wallet. An order can hold tickets across
 * two instances, so `uid` alone collides — always scope it by instance.
 */
export const orderKey = (
  orderUid: string | undefined,
  instanceUid: string | undefined,
): string => `${instanceUid ?? ''}:${orderUid ?? ''}`

/** `order`/`instance` query pair for channel links; omits an absent instance. */
export const channelOrderParams = (
  orderUid: string | undefined,
  instanceUid: string | undefined,
): string => {
  const params = new URLSearchParams()
  if (orderUid) params.set('order', orderUid)
  if (instanceUid) params.set('instance', instanceUid)
  return params.toString()
}

export const isSameOrder = (
  order: Order | undefined,
  orderUid: string | undefined,
  instanceUid: string | undefined,
): boolean => {
  if (!order || !orderUid) return false
  if (order.uid !== orderUid) return false
  // Without an instance to scope by, fall back to uid alone.
  if (!instanceUid) return true
  return order.instance_uid === instanceUid
}

/**
 * Flattens bookings to their orders, stamping each with its owning instance.
 *
 * Everything the wallet returns is upcoming, so `upcomingOrders` is just
 * `allOrders`. The backend already applies the wallet/memory cutoff (show start
 * + event duration + a grace buffer) when building the payload, so a booking
 * being present here *is* the signal that it is still live. Re-deriving that
 * cutoff client-side from `start_time` alone used to hide tickets for the whole
 * run of the show plus the grace window — dropped from the stack, but not yet
 * in memories, so they showed up nowhere. Memories come from /order/memory,
 * never from the wallet.
 */
export const flattenWalletOrders = (
  bookings: Booking[] | undefined,
): SplitOrders => {
  const allOrders: Order[] = []

  for (const booking of bookings ?? []) {
    for (const order of booking.orders ?? []) {
      // Stamp the owning instance so flattened orders stay disambiguated.
      const scoped: Order = order.instance_uid
        ? order
        : { ...order, instance_uid: booking.instance_uid }
      allOrders.push(scoped)
    }
  }

  return { allOrders, upcomingOrders: allOrders }
}
